<template>
    <div class="h-screen flex">
        <!-- Sfondo Animato -->
        <AnimatedBackground />

        <!-- Sidebar desktop - nascosta nel login -->
        <div 
            v-if="!isLoginRoute"
            class="hidden lg:block border-r border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 relative z-10"
        >
        </div>

        <!-- Overlay per mobile -->
        <Transition
            enter-active-class="transition-opacity ease-linear duration-300"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-opacity ease-linear duration-300"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div 
                v-if="showMobileMenu && !isLoginRoute" 
                class="fixed inset-0 bg-surface-900/50 backdrop-blur-sm lg:hidden z-[70]"
                @click="showMobileMenu = false"
            />
        </Transition>

        <!-- Sidebar mobile -->
        <Transition
            enter-active-class="transition ease-in-out duration-300 transform"
            enter-from-class="-translate-x-full"
            enter-to-class="translate-x-0"
            leave-active-class="transition ease-in-out duration-300 transform"
            leave-from-class="translate-x-0"
            leave-to-class="-translate-x-full"
        >
            <div 
                v-if="showMobileMenu && !isLoginRoute" 
                class="fixed inset-y-0 left-0 w-80 bg-white dark:bg-surface-800 shadow-xl lg:hidden z-[80]"
            >
            </div>
        </Transition>

        <!-- Main content -->
        <main class="flex-1 flex flex-col overflow-y-auto" :class="isDark ? 'gradient-dark' : 'gradient-light'">
            <!-- Header Globale -->
            <header v-if="!isLoginRoute" class="sticky top-0 z-50">
                <!-- Header Background with Noise Texture -->
                <div class="relative">
                    <!-- Blur Background -->
                    <div class="absolute inset-0 !bg-white/40 dark:!bg-gray-950/40 !backdrop-blur-xl"></div>
                    
                    <!-- Noise Texture -->
                    <div class="absolute inset-0 opacity-20 dark:opacity-30" style="background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIHR5cGU9ImZyYWN0YWxOb2lzZSIgYmFzZUZyZXF1ZW5jeT0iLjc1IiBzdGl0Y2hUaWxlcz0ic3RpdGNoIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIiBmaWx0ZXI9InVybCgjYSkiLz48L3N2Zz4=')"></div>

                    <!-- Gradient Border -->
                    <div class="absolute bottom-0 left-0 right-0 h-[1px]">
                        <div class="absolute inset-0 !bg-gradient-to-r !from-transparent via-blue-500/30 dark:via-blue-400/30 !to-transparent"></div>
                    </div>

                    <!-- Content -->
                    <div class="relative mx-auto h-16">
                        <div class="h-full px-6 flex items-center justify-between">
                            <!-- Logo e Titolo -->
                            <div class="flex items-center space-x-4 group logo-container">
                                <div class="relative">
                                    <!-- Logo Container -->
                                    <div class="relative w-10 h-10 !rounded-lg overflow-hidden !bg-gradient-to-tr !from-indigo-500 !via-blue-500 !to-sky-500 !shadow-lg !shadow-blue-500/20 dark:!shadow-blue-500/10">
                                        <div class="absolute inset-[2px] !rounded-[6px] !bg-white dark:!bg-gray-900 flex items-center justify-center">
                                            <div class="relative z-10 flex items-center justify-center !w-6 !h-6 !bg-gradient-to-br !from-indigo-500 !via-blue-500 !to-sky-500 !rounded-md group-hover:!scale-110 !transition-all !duration-300">
                                                <i class="pi pi-server !text-white text-sm"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div class="flex flex-col">
                                    <h1 class="text-lg font-bold !text-gray-900 dark:!text-white group-hover:!text-blue-600 dark:group-hover:!text-blue-400 !transition-colors !duration-300">
                                        Log Service
                                    </h1>
                                    <span class="text-xs font-medium !text-gray-500 dark:!text-gray-400">Monitoring System</span>
                                </div>
                            </div>

                            <!-- Barra di Ricerca Globale -->
                            <div class="flex-1 max-w-xl mx-8" v-if="!isDashboard">
                                <div class="relative group">
                                    <div class="relative">
                                        <InputText 
                                            v-model="globalSearch" 
                                            placeholder="Cerca database o collezioni..."
                                            class="w-full !h-10 !rounded-lg !pl-11 !pr-4 !bg-gray-50 dark:!bg-gray-800/50 !border !border-gray-200 dark:!border-gray-700 focus:!border-blue-500 dark:focus:!border-blue-400 !shadow-sm hover:!border-gray-300 dark:hover:!border-gray-600 !transition-all !duration-300"
                                            @focus="showResults = true"
                                            @blur="handleSearchBlur"
                                        />
                                        <div class="absolute left-3 top-1/2 -translate-y-1/2">
                                            <i class="pi pi-search !text-gray-400 dark:!text-gray-500"></i>
                                        </div>
                                    </div>

                                    <!-- Dropdown Risultati -->
                                    <div v-if="showResults && filteredDatabases.length > 0"
                                         class="absolute top-full left-0 right-0 mt-2 !bg-white dark:!bg-gray-800 !rounded-lg !border !border-gray-200 dark:!border-gray-700 !divide-y !divide-gray-100 dark:!divide-gray-700 !shadow-lg overflow-hidden z-50">
                                        <div class="max-h-72 overflow-y-auto">
                                            <div v-for="db in filteredDatabases" 
                                                 :key="db.name"
                                                 @click="selectDatabase(db)"
                                                 class="flex items-center gap-3 p-3 hover:!bg-gray-50 dark:hover:!bg-gray-700/50 cursor-pointer !transition-colors !duration-200">
                                                <div class="!w-8 !h-8 !rounded-md !bg-blue-50 dark:!bg-blue-500/10 flex items-center justify-center">
                                                    <i class="pi pi-database !text-blue-600 dark:!text-blue-400"></i>
                                                </div>
                                                <div class="flex-1 min-w-0">
                                                    <div class="font-medium !text-gray-900 dark:!text-white truncate">
                                                        {{ formatDatabaseName(db.name) }}
                                                    </div>
                                                    <div class="text-sm !text-gray-500 dark:!text-gray-400 truncate">
                                                        {{ getDatabaseDescription(db.name) }}
                                                    </div>
                                                </div>
                                                <i class="pi pi-chevron-right !text-gray-400 dark:!text-gray-500"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Breadcrumb e Azioni -->
                            <div class="flex items-center gap-6">
                                <router-link 
                                        to="/remote-logs" 
                                        class="hover:!text-green-600 dark:hover:!text-green-400 text-green-600 dark:text-green-400 text-sm !transition-colors !duration-300"
                                    >
                                        Log Remoti
                                    </router-link>
                                <!-- Breadcrumb -->
                                <div class="hidden md:flex items-center space-x-2 text-sm !text-blue-600/60 dark:!text-blue-400/60">
                                
                                    <router-link 
                                        to="/" 
                                        class="hover:!text-blue-600 dark:hover:!text-blue-400 !transition-colors !duration-300"
                                    >
                                        Dashboard
                                    </router-link>
                                    <template v-if="route.name !== 'dashboard' && route.name !== 'remoteLogs'">
                                        <i class="pi pi-angle-right !text-blue-600/40 dark:!text-blue-400/40 text-xs"></i>
                                        <span class="!text-blue-600/80 dark:!text-blue-400/80">{{ route.name }}</span>
                                    </template>
                                </div>

                                <!-- Azioni -->
                                <div class="flex items-center gap-3">
                                    <ToggleTheme size="sm" />
                                    
                                    <!-- Logout -->
                                    <button 
                                        @click="handleLogout"
                                        class="!p-2.5 !rounded-xl !bg-gradient-to-br !from-red-50 !to-red-100/50 dark:!from-red-500/10 dark:!to-red-400/10 hover:!scale-95 !transition-all !duration-300 group"
                                        title="Logout"
                                    >
                                        <i class="pi pi-sign-out !text-red-500/60 dark:!text-red-400/60 group-hover:!text-red-500 dark:group-hover:!text-red-400 !transition-colors !duration-300"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <!-- Page content -->
            <div class="flex-1">
                <RouterView v-slot="{ Component }">
                    <Transition 
                        name="fade" 
                        mode="out-in"
                        appear
                    >
                        <div :key="route.path" class="h-full">
                            <component :is="Component" />
                        </div>
                    </Transition>
                </RouterView>
            </div>
        </main>
    </div>
</template>

<script setup>
import { ref, computed, provide, watch, onMounted } from 'vue';
import { useDark } from '@vueuse/core';
import { useRoute, useRouter } from 'vue-router';
import AnimatedBackground from '@/components/backgrounds/AnimatedBackground.vue';
import { useAuthStore } from '@/stores/auth';
import { useLogStore } from '@/stores/log.store';
import ToggleTheme from '@/components/reusable/ToggleTheme.vue';
import InputText from 'primevue/inputtext';

const router = useRouter();
const route = useRoute();
const isDark = useDark();
const showMobileMenu = ref(false);
const authStore = useAuthStore();
const logStore = useLogStore();
const globalSearch = ref('');
const showResults = ref(false);
const isSearchExiting = ref(false);

// Provide la ricerca globale per altri componenti
provide('globalSearch', globalSearch);

// Computed per filtrare i database
const filteredDatabases = computed(() => {
    const databases = logStore.getDatabases;
    if (!globalSearch.value) return [];
    return databases.filter(db => 
        db.name.toLowerCase().includes(globalSearch.value.toLowerCase())
    );
});

// Formatta il nome del database
const formatDatabaseName = (name) => {
    return name.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
};

// Ottiene la descrizione del database
const getDatabaseDescription = (name) => {
    const type = name.toLowerCase();
    if (type.includes('stack')) return 'Database Stack';
    if (type.includes('service')) return 'Database Servizio';
    return 'Database';
};

// Seleziona un database
const selectDatabase = (db) => {
    logStore.setSelectedDatabase(db);
    router.push({
        name: 'logs',
        query: { db: db.name }
    });
    globalSearch.value = '';
    showResults.value = false;
};

// Carica i database all'avvio
onMounted(async () => {
    // Carica i database solo se non siamo nella pagina di login
    if (!isLoginRoute.value && authStore.isAuthenticated) {
        await logStore.fetchDatabases();
    }
});

const handleLogout = async () => {
    await authStore.logout();
    router.push('/login');
};

// Computed per verificare se siamo nella pagina di login
const isLoginRoute = computed(() => route.name === 'login');

// Computed per verificare se siamo nella dashboard
const isDashboard = computed(() => route.name === 'dashboard');

// Computed per lo stile della barra di ricerca
const searchBarStyle = computed(() => {
    if (!isDashboard.value) return {};
    
    return {
        transform: 'translateY(0)',
        opacity: 1,
        transition: 'all 0.5s ease-in-out'
    };
});

// Funzione per gestire il blur della ricerca
const handleSearchBlur = () => {
    setTimeout(() => {
        showResults.value = false;
    }, 200);
};
</script>

<style>
/* Transizioni di pagina */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* Theme transitions */
:root {
    --transition-duration: 0.3s;
}

* {
    transition: background-color var(--transition-duration) ease,
                border-color var(--transition-duration) ease,
                color var(--transition-duration) ease,
                transform var(--transition-duration) ease;
}

/* Header e Logo */
.logo-container {
    @apply !transition-all !duration-500 hover:!translate-x-0.5;
}

.logo-container:hover .logo-glow {
    @apply !opacity-100 !scale-110;
}

/* Stili per la barra di ricerca nell'header */
.search-bar-container {
    @apply !transition-all !duration-300 !relative;
}

.search-bar-container::before {
    content: '';
    @apply absolute -inset-0.5 !bg-gradient-to-r !from-violet-600/0 !via-blue-600/5 !to-sky-400/0 
           opacity-0 !rounded-xl !transition-all !duration-500;
}

.search-bar-container:hover::before {
    @apply !opacity-100 !scale-[1.02];
}

.search-bar-container .dashboard-search input {
    @apply !text-gray-900 dark:!text-white 
           !bg-gradient-to-br !from-white/80 !to-blue-50/80 dark:!from-gray-900/80 dark:!to-gray-800/80
           hover:!from-white/90 hover:!to-blue-50/90 dark:hover:!from-gray-900/90 dark:hover:!to-gray-800/90
           focus:!from-white focus:!to-blue-50 dark:focus:!from-gray-900 dark:focus:!to-gray-800
           !backdrop-blur-xl
           !transition-all !duration-300;
}

.search-bar-container:hover .dashboard-search input {
    @apply !shadow-lg !shadow-blue-600/5 dark:!shadow-blue-400/5 !scale-[1.01];
}

.search-bar-container:focus-within .dashboard-search input {
    @apply !scale-[1.02];
}

/* Dropdown Risultati con effetto vetro e sfumature */
.search-bar-container .search-results {
    @apply !transition-all !duration-500 !transform;
    clip-path: inset(0% 0% 0% 0% round 1rem);
}

.search-results-enter-active,
.search-results-leave-active {
    @apply !transition-all !duration-300;
}

.search-results-enter-from,
.search-results-leave-to {
    @apply !opacity-0 !-translate-y-2 !scale-95;
    clip-path: inset(0% 100% 100% 0% round 1rem);
}

/* Custom Scrollbar più sottile e elegante */
.custom-scrollbar::-webkit-scrollbar {
    @apply !w-1;
}

.custom-scrollbar::-webkit-scrollbar-track {
    @apply !bg-transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
    @apply !bg-gradient-to-b !from-blue-600/20 !to-violet-600/20 dark:!from-blue-400/20 dark:!to-violet-400/20
           !rounded-full 
           !transition-colors !duration-300;
}

.custom-scrollbar:hover::-webkit-scrollbar-thumb {
    @apply !from-blue-600/30 !to-violet-600/30 dark:!from-blue-400/30 dark:!to-violet-400/30;
}

/* Animazioni per i risultati */
@keyframes result-appear {
    0% {
        @apply !opacity-0 !-translate-y-2 !scale-95 !blur-sm;
    }
    100% {
        @apply !opacity-100 !translate-y-0 !scale-100 !blur-0;
    }
}

.search-bar-container .max-h-72 > div {
    animation: result-appear 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.search-bar-container .max-h-72 > div:nth-child(2) { animation-delay: 0.1s; }
.search-bar-container .max-h-72 > div:nth-child(3) { animation-delay: 0.15s; }
.search-bar-container .max-h-72 > div:nth-child(4) { animation-delay: 0.2s; }
.search-bar-container .max-h-72 > div:nth-child(5) { animation-delay: 0.25s; }

/* Effetti Hover per i risultati */
.search-bar-container .max-h-72 > div {
    @apply !relative !overflow-hidden;
}

.search-bar-container .max-h-72 > div::before {
    content: '';
    @apply absolute inset-0 !bg-gradient-to-r !from-blue-600/0 via-blue-600/5 !to-blue-600/0
           opacity-0 !transition-all !duration-300 !scale-x-95;
}

.search-bar-container .max-h-72 > div:hover::before {
    @apply !opacity-100 !scale-x-100;
}

/* Miglioramenti Dark Mode */
.dark .search-bar-container::before {
    @apply !from-blue-400/0 via-blue-400/5 !to-blue-400/0;
}

.dark .search-bar-container .max-h-72 > div::before {
    @apply !from-blue-400/0 via-blue-400/5 !to-blue-400/0;
}

/* Glassmorphism Effects */
.glassmorphism {
    @apply !bg-white/60 dark:!bg-gray-900/40
           !backdrop-blur-xl
           !border !border-black/[0.04] dark:!border-white/[0.04]
           !shadow-lg !shadow-black/[0.03] dark:!shadow-black/10
           !transition-all !duration-300;
}

.glassmorphism:hover {
    @apply !bg-white/80 dark:!bg-gray-900/60
           !border-black/[0.06] dark:!border-white/[0.06]
           !shadow-xl !scale-[1.02];
}

/* Button Effects */
.action-button {
    @apply !transition-all !duration-300 hover:!scale-95;
}

.action-button:active {
    @apply !scale-90;
}

/* Noise Effect */
.noise-bg {
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIHR5cGU9ImZyYWN0YWxOb2lzZSIgYmFzZUZyZXF1ZW5jeT0iLjc1IiBzdGl0Y2hUaWxlcz0ic3RpdGNoIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIiBmaWx0ZXI9InVybCgjYSkiLz48L3N2Zz4=');
    @apply !opacity-20 dark:!opacity-30;
}
</style>